// One camera, for every process diagram.
//
// A process is wider than the rail it is drawn in, so both diagrams — the
// overview's SVG and the trail's design view — have to pan and zoom. They draw
// different things, but "where is the reader looking" is the same question, so
// the state and the gestures live here and each surface only decides what to
// apply the transform to.
//
// Gestures, and why these:
//   * DRAG pans, always — including when the whole diagram already fits.
//     Nudging a fitted picture aside is a thing people do, and a drag that
//     moves nothing reads as broken.
//   * ⌘/Ctrl + WHEEL zooms about the cursor. That is also what a trackpad
//     pinch sends, so pinch works for free.
//   * A PLAIN wheel is left alone: these diagrams sit inside scrolling panes,
//     and a canvas that eats the scroll traps the page.
//   * The keyboard does both, so the diagram is not mouse-only.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react'

export const MIN_SCALE = 0.4
export const MAX_SCALE = 6

export interface Camera {
  /** Screen-space transform: translate first, then scale. */
  x: number
  y: number
  scale: number
}

const HOME: Camera = { x: 0, y: 0, scale: 1 }

export interface DiagramCamera {
  cam: Camera
  /** `translate(x, y) scale(s)` — valid in both CSS and SVG. */
  transform: string
  /** True while the reader is dragging, for the cursor. */
  grabbing: boolean
  /** Attach to the element the reader drags — it also binds the wheel. */
  bind: {
    ref: (el: Element | null) => void
    onPointerDown: (e: React.PointerEvent) => void
    onPointerMove: (e: React.PointerEvent) => void
    onPointerUp: (e: React.PointerEvent) => void
    onPointerCancel: (e: React.PointerEvent) => void
  }
  /** Attach to a focusable wrapper for the keyboard. */
  onKeyDown: (e: React.KeyboardEvent) => void
  zoomBy: (factor: number, at?: { x: number; y: number }) => void
  reset: () => void
  /** True once the reader has moved or zoomed — "fit" is then an action. */
  moved: boolean
  /** True when the LAST pointer gesture actually dragged, so a click that
   *  ended a pan does not also select what it landed on. */
  dragged: React.MutableRefObject<boolean>
}

export function useDiagramCamera(enabled = true): DiagramCamera {
  const [cam, setCam] = useState<Camera>(HOME)
  const [grabbing, setGrabbing] = useState(false)
  const elRef = useRef<Element | null>(null)
  const drag = useRef<{ x: number; y: number } | null>(null)
  const dragged = useRef(false)

  const zoomBy = useCallback((factor: number, at?: { x: number; y: number }) => {
    const rect = elRef.current?.getBoundingClientRect()
    setCam((c) => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, c.scale * factor))
      if (Math.abs(next - c.scale) < 1e-6) return c
      // Hold the point under the cursor still: it is what the reader meant to
      // look closer at. Without one (buttons, keyboard) hold the centre.
      const px = at && rect ? at.x - rect.left : (rect?.width || 0) / 2
      const py = at && rect ? at.y - rect.top : (rect?.height || 0) / 2
      const k = next / c.scale
      return { scale: next, x: px - (px - c.x) * k, y: py - (py - c.y) * k }
    })
  }, [])

  const reset = useCallback(() => setCam(HOME), [])

  useEffect(() => {
    const el = elRef.current
    if (!el || !enabled) return
    const onWheel = (e: Event) => {
      const w = e as WheelEvent
      if (!w.ctrlKey && !w.metaKey) return
      w.preventDefault()
      zoomBy(Math.exp(-w.deltaY * 0.002), { x: w.clientX, y: w.clientY })
    }
    // By hand, because React's own wheel listener is passive and a passive
    // listener cannot preventDefault — the page would scroll as well.
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [enabled, zoomBy])

  const bind = {
    ref: (el: Element | null) => { elRef.current = el },
    onPointerDown: (e: React.PointerEvent) => {
      if (!enabled || e.button !== 0) return
      // NO pointer capture here. Capturing on press retargets the click that
      // follows to the capturing element, which silently ate every click on a
      // step, a record and a kebab inside the diagram. Capture is taken on the
      // first real MOVE instead — by then the gesture is a drag, and a drag has
      // no click to lose.
      drag.current = { x: e.clientX, y: e.clientY }
      dragged.current = false
      setGrabbing(true)
    },
    onPointerMove: (e: React.PointerEvent) => {
      const d = drag.current
      if (!d) return
      const dx = e.clientX - d.x
      const dy = e.clientY - d.y
      if (!dragged.current && Math.abs(dx) + Math.abs(dy) < 3) return
      if (!dragged.current) {
        // Now it is a drag: hold the pointer so it keeps panning past the edge
        // of the diagram and does not stop on the first thing it crosses.
        try { (e.currentTarget as Element).setPointerCapture(e.pointerId) } catch { /* not captured */ }
      }
      dragged.current = true
      d.x = e.clientX
      d.y = e.clientY
      setCam((c) => ({ ...c, x: c.x + dx, y: c.y + dy }))
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!drag.current) return
      drag.current = null
      setGrabbing(false)
      try {
        const el = e.currentTarget as Element
        if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
      } catch { /* never captured */ }
    },
    onPointerCancel: (e: React.PointerEvent) => {
      drag.current = null
      setGrabbing(false)
      try {
        const el = e.currentTarget as Element
        if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
      } catch { /* never captured */ }
    },
  }

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!enabled) return
    const step = 44
    const moves: Record<string, () => void> = {
      ArrowLeft: () => setCam((c) => ({ ...c, x: c.x + step })),
      ArrowRight: () => setCam((c) => ({ ...c, x: c.x - step })),
      ArrowUp: () => setCam((c) => ({ ...c, y: c.y + step })),
      ArrowDown: () => setCam((c) => ({ ...c, y: c.y - step })),
      '+': () => zoomBy(1.2),
      '=': () => zoomBy(1.2),
      '-': () => zoomBy(1 / 1.2),
      '0': () => setCam(HOME),
    }
    const hit = moves[e.key]
    if (!hit) return
    e.preventDefault()
    hit()
  }, [enabled, zoomBy])

  return {
    cam,
    transform: `translate(${cam.x}, ${cam.y}) scale(${cam.scale})`,
    grabbing,
    bind,
    onKeyDown,
    zoomBy,
    reset,
    moved: cam.x !== 0 || cam.y !== 0 || cam.scale !== 1,
    dragged,
  }
}

/** The −/+/fit cluster. Small, quiet, and out of the way of the picture. */
export function CameraControls({ camera, style }: {
  camera: DiagramCamera
  style?: React.CSSProperties
}) {
  const { cam, zoomBy, reset, moved } = camera
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 7,
      border: '1px solid var(--color-border)', background: 'var(--color-bg, #fff)',
      opacity: 0.94, ...style,
    }}>
      {cam.scale !== 1 && (
        <span style={{
          fontSize: 13, color: 'var(--color-text-secondary)', padding: '0 4px',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {Math.round(cam.scale * 100)}%
        </span>
      )}
      <CamButton label="Zoom out (⌘/Ctrl + scroll)" onClick={() => zoomBy(1 / 1.25)}
        disabled={cam.scale <= MIN_SCALE + 1e-6}>
        <ZoomOut size={13} />
      </CamButton>
      <CamButton label="Zoom in (⌘/Ctrl + scroll)" onClick={() => zoomBy(1.25)}
        disabled={cam.scale >= MAX_SCALE - 1e-6}>
        <ZoomIn size={13} />
      </CamButton>
      <CamButton label="Fit the whole process" onClick={reset} disabled={!moved}>
        <Maximize2 size={13} />
      </CamButton>
    </div>
  )
}

function CamButton({ label, onClick, disabled, children }: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 22, height: 22, padding: 0, borderRadius: 5, border: 0,
        cursor: disabled ? 'default' : 'pointer', background: 'transparent',
        color: 'var(--color-text-secondary)', opacity: disabled ? 0.35 : 1,
      }}
    >
      {children}
    </button>
  )
}
